import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { CloseFill, Menu2 } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { IconButton, Stack, Tabs, Typography } from '@lyearn/ui';
import { ViewMode } from '@/modules/knowledge-base-copilot/types';
import { tabMobileStylesClassName } from '@/modules/learner/views/LearnerProfile/styles';

import { NoteFragment } from '../../graphql/queries/generated/notes';
import { mobileTabsStyles } from './styles';

interface ChatHeaderProps {
  note: NoteFragment | null;
  channelTitle: string;
  onClose: () => void;
  onAction: (value: ViewMode) => void;
  viewMode: ViewMode;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  updatedTitle: string | undefined;
  currentTab: MobileTabs;
  setCurrentTab: React.Dispatch<React.SetStateAction<MobileTabs>>;
  setShowListDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

export enum MobileTabs {
  Chat = 'Chat',
  Notes = 'Notes',
}

function ChatHeader({
  note,
  channelTitle,
  updatedTitle,
  onClose,
  onAction,
  setViewMode,
  currentTab,
  setCurrentTab,
  viewMode,
  setShowListDrawer,
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const { isMobile } = useScreen();

  const mobileTabs = [
    {
      label: <Typography variant="body-short-01">{t('Common:KnowledgeBase.Chat')}</Typography>,
      value: MobileTabs.Chat,
    },
    {
      label: <Typography variant="body-short-01">{t('Common:KnowledgeBase.Notes')}</Typography>,
      value: MobileTabs.Notes,
    },
  ];

  const handleTabChange = (newTab: MobileTabs) => {
    if (newTab === MobileTabs.Chat) {
      setViewMode(ViewMode.Chat);
    } else {
      setViewMode(ViewMode.Notes);
    }
    setCurrentTab(newTab);
  };

  return (
    <Stack
      className="px-16 py-12 sticky top-0 bg-bg-primary border-b border-border xs:border-none z-10"
      direction="col">
      <Stack
        alignItems="center"
        className={cn('w-full h-[3.2rem]', isMobile ? 'mb-8' : '')}
        justifyContent="between">
        <Stack alignItems="center" className="w-full mx-auto pl-4" justifyContent="start">
          {isMobile ? (
            <Stack className="flex-shrink-0">
              <Menu2
                className="mr-16"
                fillColorToken="icon-active"
                height={26}
                width={26}
                onClick={() => setShowListDrawer(true)}
              />
            </Stack>
          ) : null}
          <Stack alignItems="center" gapX={8}>
            {viewMode === ViewMode.Content && updatedTitle ? (
              <Typography numberOfLines={1} variant="heading-xs">
                {updatedTitle || 'Pinned Item'}
              </Typography>
            ) : null}
            {viewMode === ViewMode.Notes && note ? (
              <Typography numberOfLines={1} variant="heading-xs">
                {updatedTitle || note?.title}
              </Typography>
            ) : null}
            {viewMode === ViewMode.Chat ? (
              <Typography numberOfLines={1} variant={isMobile ? 'heading-s' : 'heading-xs'}>
                {channelTitle ? channelTitle : t('Common:KnowledgeBase.NewChat')}
              </Typography>
            ) : null}
          </Stack>
        </Stack>

        <IconButton
          Icon={CloseFill}
          appearance="primary"
          shape="square"
          size="xl"
          onClick={onClose}
        />
      </Stack>
      {isMobile && (
        <>
          <Stack className="h-1 bg-border my-8 w-auto -mx-16" />
          <Stack alignItems="center" className="w-full">
            <Tabs
              className={cn('w-full', tabMobileStylesClassName, mobileTabsStyles)}
              tabs={mobileTabs}
              value={currentTab}
              onChange={handleTabChange}
            />
          </Stack>
          <Stack className="h-1 bg-border -mt-1 -mb-12 w-auto -mx-16" />
        </>
      )}
    </Stack>
  );
}

export default ChatHeader;
